import { MutationTree } from 'vuex';
import { State } from './state';
import { ClientViewMutationTypes } from './mutation-types';
import {
  ClientDetailsApiResponse,
  ClientFamilyApiResponse,
  PortfoliosApiResult,
  PortfolioApiResponse,
  ScenarioCoreDetailsResponse,
  ScenarioResponse,
  Scenario,
  StandardFeesApiResponse,
} from './types';
import { reduce } from 'lodash';
import { FormFieldConfig } from '@/types/Client';

export type Mutations<S = State> = {
  [ClientViewMutationTypes.SET_PORTFOLIO_LIST](
    state: S,
    payload: {
      portfolios: Array<PortfoliosApiResult>;
    }
  ): void;
  [ClientViewMutationTypes.SET_PORTFOLIOS_LOADING](
    state: S,
    isLoading: boolean
  ): void;
  [ClientViewMutationTypes.SET_CLIENT_DETAILS](
    state: S,
    payload: ClientDetailsApiResponse
  ): void;
  [ClientViewMutationTypes.SET_CLIENT_FAMILY](
    state: S,
    payload: ClientFamilyApiResponse
  ): void;
  [ClientViewMutationTypes.CLEAR_STATE](state: S, payload: null): void;
  [ClientViewMutationTypes.SET_PORTFOLIO](
    state: S,
    payload: {
      portfolio: PortfolioApiResponse;
    }
  ): void;
  [ClientViewMutationTypes.SET_PORTFOLIO_LOADING](
    state: S,
    isLoading: boolean
  ): void;
  [ClientViewMutationTypes.SET_SCENARIO_LIST](
    state: S,
    payload: Array<ScenarioCoreDetailsResponse>
  ): void;
  [ClientViewMutationTypes.SET_SCENARIO](
    state: S,
    payload: ScenarioResponse
  ): void;
  [ClientViewMutationTypes.SET_SCENARIO_LOADING](
    state: S,
    isLoading: boolean
  ): void;
  [ClientViewMutationTypes.SET_SCENARIO_SAVING](
    state: S,
    isSaving: boolean
  ): void;
  [ClientViewMutationTypes.SET_CLIENT_LOADING](
    state: S,
    isLoading: boolean
  ): void;
  [ClientViewMutationTypes.SET_CLIENT_ERROR](state: S, isError: boolean): void;
};

export const mutations: MutationTree<State> & Mutations = {
  [ClientViewMutationTypes.SET_PORTFOLIO_LIST](
    state: State,
    payload: {
      portfolios: Array<PortfoliosApiResult>;
    }
  ) {
    state.portfolios = payload.portfolios;
  },
  [ClientViewMutationTypes.SET_PORTFOLIOS_LOADING](
    state: State,
    isLoading: boolean
  ) {
    state.portfoliosIsLoading = isLoading;
  },
  [ClientViewMutationTypes.SET_CLIENT_DETAILS](
    state: State,
    payload: ClientDetailsApiResponse
  ) {
    state.clientDetails = payload;
  },
  [ClientViewMutationTypes.SET_CLIENT_FAMILY](
    state: State,
    payload: ClientFamilyApiResponse
  ) {
    state.clientFamily = payload;
  },
  [ClientViewMutationTypes.CLEAR_STATE](state: State) {
    state.clientDetails = {} as ClientDetailsApiResponse;
    state.clientFamily = {} as ClientFamilyApiResponse;
    state.portfolio = {} as PortfolioApiResponse;
    state.portfolios = {} as Array<PortfoliosApiResult>;
    state.standardFees = {} as PortfolioApiResponse;
    state.scenario = {} as Scenario;
  },
  [ClientViewMutationTypes.SET_PORTFOLIO](
    state: State,
    payload: {
      portfolio: PortfolioApiResponse;
    }
  ) {
    state.portfolio = payload.portfolio;
  },
  [ClientViewMutationTypes.SET_PORTFOLIO_LOADING](
    state: State,
    isLoading: boolean
  ) {
    state.portfolioIsLoading = isLoading;
  },
  [ClientViewMutationTypes.SET_STANDARD_FEES](
    state: State,
    payload: {
      standardFees: PortfolioApiResponse;
    }
  ) {
    state.standardFees = payload.standardFees;
  },
  [ClientViewMutationTypes.SET_SCENARIO_LIST](
    state: State,
    payload: Array<ScenarioCoreDetailsResponse>
  ) {
    state.scenarios = payload;
  },
  [ClientViewMutationTypes.SET_SCENARIO](
    state: State,
    payload: ScenarioResponse
  ) {
    const fieldConfig: { [key: string]: FormFieldConfig } = reduce(
      payload.fieldConfig,
      (result, value) => {
        const key = value.fieldKey;
        result[key] = value;
        return result;
      },
      {}
    );
    state.scenario = {
      scenario: payload.scenario,
      pricing: payload.pricing,
      fieldConfig: fieldConfig,
    } as Scenario;
    state.standardFees = payload.standardFees as StandardFeesApiResponse;
  },
  [ClientViewMutationTypes.SET_SCENARIO_LOADING](
    state: State,
    isLoading: boolean
  ) {
    state.scenarioIsLoading = isLoading;
  },
  [ClientViewMutationTypes.SET_SCENARIO_SAVING](
    state: State,
    isSaving: boolean
  ) {
    state.scenarioIsSaving = isSaving;
  },
  [ClientViewMutationTypes.SET_CLIENT_LOADING](
    state: State,
    isLoading: boolean
  ) {
    state.clientIsLoading = isLoading;
  },
  [ClientViewMutationTypes.SET_CLIENT_ERROR](state: State, isError: boolean) {
    state.clientError = isError;
  },
};
